import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminProductService } from '../services/adminService';
import SessionTimeout from '../components/SessionTimeout';
import './styles/AdminProductForm.css';

const AdminProductForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    tags: [] as string[],
    inventory: '',
    image: ''
  });

  const [newCategoryName, setNewCategoryName] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const [categories, setCategories] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const loadMetadata = useCallback(async () => {
    try {
      const [cats, tags] = await Promise.all([
        adminProductService.getCategories(),
        adminProductService.getTags()
      ]);
      setCategories(cats);
      setAvailableTags(tags);
    } catch (err: any) {
      setError('Failed to load metadata');
    }
  }, []);

  const loadProduct = useCallback(async (productId: string) => {
    try {
      const products = await adminProductService.getProducts();
      const product = products.find(p => p.productId === productId);
      if (product) {
        setFormData({
          name: product.name,
          description: product.description || '',
          price: product.price.toString(),
          category: product.category,
          tags: product.tags || [],
          inventory: product.inventory?.toString() || '',
          image: product.image || ''
        });
      }
    } catch (err: any) {
      setError('Failed to load product');
    }
  }, []);

  useEffect(() => {
    loadMetadata();
    if (isEdit && id) {
      loadProduct(id);
    }
  }, [id, isEdit, loadMetadata, loadProduct]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let imageUrl = formData.image;
      let s3Key = '';
      
      // Upload new image if file selected
      if (imageFile) {
        setError('Uploading image...');
        const uploadResult = await adminProductService.uploadImage(imageFile, formData.name);
        const parsed = JSON.parse(uploadResult);
        imageUrl = parsed.publicUrl;
        s3Key = parsed.s3Key;
      }
      
      // Validate we have an image
      if (!imageUrl) {
        setError('Please provide an image');
        setLoading(false);
        return;
      }
      
      const productData: any = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category === '__new__' ? newCategoryName : formData.category,
        tags: formData.tags,
        inventory: parseInt(formData.inventory) || 0,
        image: imageUrl
      };
      
      // Only include s3Key if we have a new upload
      if (s3Key) {
        productData.s3Key = s3Key;
      }

      if (isEdit && id) {
        await adminProductService.updateProduct(id, productData);
      } else {
        await adminProductService.createProduct(productData);
      }

      setSuccess(true);
      setTimeout(() => navigate('/admin'), 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      if (!availableTags.includes(newTag.trim())) {
        setAvailableTags(prev => [...prev, newTag.trim()]);
      }
      setNewTag('');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      
      setImageFile(file);
      setError(''); // Clear any previous errors
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };


  return (
    <div className="admin-product-form-page">
      <SessionTimeout />
      
      <div className="form-container">
        <div className="form-header">
          <button className="back-button" onClick={() => navigate('/admin')}>
            ← Back to Dashboard
          </button>
          <h1 className="form-title">{isEdit ? 'Edit Product' : 'Add New Product'}</h1>
          <p className="form-subtitle">
            {isEdit ? 'Update product details and sync with Stripe' : 'Create a new product and sync with Stripe'}
          </p>
        </div>

        {error && (
          <div className="error-banner">
            <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className="success-banner">
            <svg className="success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Product {isEdit ? 'updated' : 'created'} successfully! Redirecting...
          </div>
        )}

        <form className="product-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Product Name *
              </label>
              <input
                type="text"
                id="name"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Mushroom Trio Keyring"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Category *
              </label>
              <select
                id="category"
                className="form-input"
                value={formData.category}
                onChange={(e) => {
                  setFormData({ ...formData, category: e.target.value });
                  if (e.target.value !== '__new__') {
                    setNewCategoryName('');
                  }
                }}
                required
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
                <option value="__new__">+ Add New Category</option>
              </select>
              {formData.category === '__new__' && (
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter new category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  style={{ marginTop: '0.5rem' }}
                  autoFocus
                />
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              className="form-textarea"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detailed product description..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price" className="form-label">
                Price (AUD) *
              </label>
              <input
                type="number"
                id="price"
                className="form-input"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="inventory" className="form-label">
                Inventory *
              </label>
              <input
                type="number"
                id="inventory"
                className="form-input"
                min="0"
                value={formData.inventory}
                onChange={(e) => setFormData({ ...formData, inventory: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="image" className="form-label">
              Product Image
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label 
                htmlFor="image-file-input" 
                style={{
                  display: 'inline-block',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#4D6351',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontSize: '0.9375rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s',
                  width: 'fit-content'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3d4f40'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4D6351'}
              >
                Choose Image File
              </label>
              <input
                type="file"
                id="image-file-input"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              <span style={{ fontSize: '0.875rem', color: '#666' }}>
                {imageFile ? `Selected: ${imageFile.name}` : 'No file chosen'}
              </span>
            </div>
            {imagePreview && (
              <div style={{ marginTop: '1rem' }}>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                />
              </div>
            )}
            {!imagePreview && formData.image && (
              <div style={{ marginTop: '1rem' }}>
                <img 
                  src={formData.image} 
                  alt="Current" 
                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                />
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                  Current image - upload new file to replace
                </p>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Tags</label>
            <div className="tags-selector">
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className={`tag-button ${formData.tags.includes(tag) ? 'selected' : ''}`}
                  onClick={() => handleTagToggle(tag)}
                >
                  {formData.tags.includes(tag) && '✓ '}
                  {tag}
                </button>
              ))}
            </div>
            <div className="add-tag">
              <input
                type="text"
                className="form-input"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add custom tag..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              />
              <button type="button" className="btn-secondary" onClick={handleAddTag}>
                Add Tag
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate('/admin')}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <div className="spinner-small"></div>
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{isEdit ? 'Update Product' : 'Create Product'}</>
              )}
            </button>
          </div>

          {!isEdit && (
            <div className="info-box">
              <svg className="info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <strong>Automatic Stripe Sync</strong>
                <p>When you create this product, it will automatically be added to Stripe with the correct metadata and price.</p>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AdminProductForm;